/**
 * Hook de dados da tela Início mobile.
 *
 * Reutiliza as mesmas query keys do dashboard desktop para cache compartilhado:
 *   ['pagamentos', workspaceId, 'aggregate', competencia]
 *   ['receitas',   workspaceId, 'aggregate', competencia]
 *
 * Pendências são derivadas de dados reais (agg.pendente). Quando não há
 * pendências o array vem vazio e o card some na UI.
 *
 * TODO[Fase 1]: status da prestação vem de mock — trocar por
 *   /api/v1/workspaces/:wsId/prestacoes?competencia=YYYY-MM
 *   quando o endpoint existir.
 */

import { usePagamentosAggregate } from '@/lib/hooks/pagamentos';
import { useReceitasAggregate } from '@/lib/hooks/receitas';
import type { HomeData, PendItem, PrestStatus } from '../modulos/home/types';

/** Converte índice de mês (0-11) + ano para formato competência YYYY-MM */
export function monthIndexToCompetencia(monthIndex: number, year: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

const MES_LABEL = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * Mock de status de prestação por competência.
 * TODO[Fase 1]: substituir por query real quando existir endpoint de prestações.
 */
const MOCK_PREST_STATUS: Record<string, PrestStatus> = {
  '2026-05': 'pronta',
  '2026-04': 'concluida',
  '2026-03': 'concluida',
  '2026-02': 'concluida',
  '2026-01': 'rascunho',
};

function derivePrestStatus(competencia: string): PrestStatus {
  return MOCK_PREST_STATUS[competencia] ?? 'rascunho';
}

/**
 * Deriva pendências de dados agregados reais.
 * Retorna array vazio quando não há nada pendente — o card não aparece.
 */
function derivePendencias(
  pendenteCount: number,
  pendenteTotalBrl: number,
  monthIndex: number,
): PendItem[] {
  const items: PendItem[] = [];

  if (pendenteCount > 0) {
    const noun = pendenteCount === 1 ? 'pagamento pendente' : 'pagamentos pendentes';
    items.push({
      id: 'pag-pending',
      label: `${pendenteCount} ${noun}`,
      sub: `R$ ${pendenteTotalBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · ${MES_LABEL[monthIndex]}`,
      tone: 'warn',
      link: { to: '/m/lancamentos', search: { filter: 'pending' } },
    });
  }

  return items;
}

interface UseHomeDataParams {
  workspaceId: string | null | undefined;
  monthIndex: number;
  year: number;
}

interface UseHomeDataResult {
  data: HomeData | null;
  isLoading: boolean;
  error: Error | null;
}

export function useHomeData({
  workspaceId,
  monthIndex,
  year,
}: UseHomeDataParams): UseHomeDataResult {
  const competencia = monthIndexToCompetencia(monthIndex, year);

  const pagAgg = usePagamentosAggregate(workspaceId, competencia);
  const recAgg = useReceitasAggregate(workspaceId, competencia);

  const isLoading = pagAgg.isLoading || recAgg.isLoading;
  const error = pagAgg.error ?? recAgg.error ?? null;

  if (isLoading || !workspaceId) {
    return { data: null, isLoading, error };
  }

  const receitas = Number(recAgg.data?.total ?? '0');
  const despesasConciliadas = Number(pagAgg.data?.conciliado.total ?? '0');
  const despesasPendentes = Number(pagAgg.data?.pendente.total ?? '0');
  const despesas = despesasConciliadas + despesasPendentes;
  const saldo = receitas - despesasConciliadas;

  const totalLancamentos = pagAgg.data?.totalLancamentos ?? 0;
  const pendenteCount = pagAgg.data?.pendente.count ?? 0;

  const data: HomeData = {
    competencia,
    prestStatus: derivePrestStatus(competencia),
    lancamentosCount: totalLancamentos,
    valorTotal: despesas,
    receitas,
    despesas,
    saldo,
    pendencias: derivePendencias(pendenteCount, despesasPendentes, monthIndex),
  };

  return { data, isLoading: false, error };
}
